import type {
  CompareWindow,
  ContributionSlice,
  ExploreMetricView,
  ExploreScopeSelection,
  ExploreViewModel,
  PeriodWindow,
} from "@/analytics/core/types";
import type { AnalyticsRawData } from "@/analytics/core/types";
import {
  buildChartBuckets,
  buildCompareSeriesPoints,
  buildSeriesPoints,
} from "@/analytics/core/chart-series";
import {
  computeDelta,
  resolveMetricValue,
  resolveMetricValueForCompare,
} from "@/analytics/core/resolve-metric";
import {
  listRepOptions,
  listTeamOptions,
  resolveEntitySeries,
  showRepPicker,
} from "@/analytics/core/scope-entities";
import { nowToronto, resolveFetchWindow, toIsoDate } from "@/analytics/core/period-windows";
import type { DepartmentDefinition, MetricDefinition } from "@/grid/core/types";
import { exploreMetricsFor, normalizeExploreMetricId } from "@/grid/config/catalog";
import { sortByTeamRosterOrder } from "@/grid/lib/profile-photos";

import {
  buildStableColorIndexMap,
  entityColorKey,
  seriesColorForKey,
} from "@/analytics/core/chart-palette";

/** Round shares to whole percents that sum to 100 (largest remainder). */
function sharePercents(values: number[]): number[] {
  const total = values.reduce((sum, value) => sum + value, 0);
  if (total <= 0) return values.map(() => 0);

  const exact = values.map((value) => (value / total) * 100);
  const floors = exact.map((value) => Math.floor(value));
  let remainder = 100 - floors.reduce((sum, value) => sum + value, 0);
  const order = exact
    .map((value, index) => ({ index, frac: value - floors[index]! }))
    .sort((a, b) => b.frac - a.frac);

  const result = [...floors];
  for (const { index } of order) {
    if (remainder <= 0) break;
    result[index]! += 1;
    remainder -= 1;
  }
  return result;
}

function resolveMetric(department: DepartmentDefinition, metricId: string): MetricDefinition {
  const pool = exploreMetricsFor(department);
  const normalized = normalizeExploreMetricId(department, metricId);
  return pool.find((row) => row.id === normalized) ?? pool[0]!;
}

function buildCompareSeries(
  metric: MetricDefinition,
  period: PeriodWindow,
  compare: CompareWindow,
  ctx: ReturnType<typeof resolveEntitySeries>[number]["ctx"],
  raw: AnalyticsRawData,
) {
  const comparePeriod: PeriodWindow = {
    ...period,
    from: compare.from,
    to: compare.to,
    label: compare.label,
  };

  return buildCompareSeriesPoints(metric, period, comparePeriod, ctx, raw);
}

function buildMetricView(
  metric: MetricDefinition,
  period: PeriodWindow,
  compare: CompareWindow,
  selection: ExploreScopeSelection,
  raw: AnalyticsRawData,
  department: DepartmentDefinition,
): ExploreMetricView {
  const entitySpecs = resolveEntitySeries(selection, raw, department);
  const teamColorMap = buildStableColorIndexMap(
    listTeamOptions(raw, department)
      .map((team) => team.colorKey)
      .filter((key): key is string => key != null),
  );
  /** Rep chips/chart/pie share one map over all team reps so colors stay aligned. */
  const repColorMap = buildStableColorIndexMap(
    listRepOptions(raw, department, selection.teamIds).map((rep) => entityColorKey("rep", rep.id)),
  );
  const seriesColorMap = selection.repIds.length > 0 ? repColorMap : teamColorMap;
  const primaryBuckets = buildChartBuckets(period, metric);

  const series = entitySpecs.map((spec) => ({
    key: spec.key,
    label: spec.label,
    color: seriesColorForKey(spec.key, seriesColorMap),
    points: buildSeriesPoints(metric, primaryBuckets, period, spec.ctx, raw),
    comparePoints:
      entitySpecs.length > 1
        ? buildCompareSeries(metric, period, compare, spec.ctx, raw)
        : undefined,
  }));

  const primaryCtx = entitySpecs[0]!.ctx;
  let total = resolveMetricValue(metric, period, period, primaryCtx, raw);
  let compareTotal = resolveMetricValueForCompare(metric, compare, period, primaryCtx, raw);
  let contributions: ContributionSlice[] = [];

  if (entitySpecs.length > 1) {
    const entityValues = entitySpecs.map((spec, index) => {
      const row = series[index]!;
      return {
        key: row.key,
        label: row.label,
        color: row.color,
        value: resolveMetricValue(metric, period, period, spec.ctx, raw) ?? 0,
      };
    });
    total = entityValues.reduce((sum, row) => sum + row.value, 0);
    compareTotal = entitySpecs.reduce((sum, spec) => {
      const value = resolveMetricValueForCompare(metric, compare, period, spec.ctx, raw);
      return sum + (value ?? 0);
    }, 0);
    const percents = sharePercents(entityValues.map((row) => row.value));
    contributions = entityValues.map((row, index) => ({
      ...row,
      sharePct: percents[index]!,
    }));
  }

  const { delta, deltaPct } = computeDelta(total, compareTotal);

  const compareSeries = buildCompareSeries(metric, period, compare, primaryCtx, raw).map(
    (point) => ({
      ...point,
      value: point.value,
    }),
  );

  return {
    metric,
    total,
    compareTotal,
    delta,
    deltaPct,
    series,
    compareSeries,
    contributions,
  };
}

export function assembleTeamMetricViews(
  department: DepartmentDefinition,
  raw: AnalyticsRawData,
  options: {
    teamGroupId: number;
    teamName: string;
    repIds: number[];
    period: PeriodWindow;
    compare: CompareWindow;
  },
): ExploreMetricView[] {
  const selection: ExploreScopeSelection = {
    teamIds: [options.teamGroupId],
    repIds: options.repIds,
  };
  const metrics = exploreMetricsFor(department);

  return metrics.map((metric) => {
    const view = buildMetricView(
      metric,
      options.period,
      options.compare,
      selection,
      raw,
      department,
    );
    return {
      ...view,
      contributions: sortByTeamRosterOrder(options.teamName, view.contributions),
    };
  });
}

export function assembleExploreViewModel(
  department: DepartmentDefinition,
  raw: AnalyticsRawData,
  options: {
    selection: ExploreScopeSelection;
    period: PeriodWindow;
    compare: CompareWindow;
    metricId: string;
    asOf?: Date;
  },
): ExploreViewModel {
  const asOf = options.asOf ?? nowToronto();
  const metric = resolveMetric(department, options.metricId);

  return {
    departmentId: department.id,
    selection: options.selection,
    period: options.period,
    compare: options.compare,
    metricId: metric.id,
    view: buildMetricView(
      metric,
      options.period,
      options.compare,
      options.selection,
      raw,
      department,
    ),
    teamOptions: listTeamOptions(raw, department),
    repOptions: listRepOptions(raw, department, options.selection.teamIds),
    showRepPicker: showRepPicker(options.selection),
    asOf: toIsoDate(asOf),
    fetchedAt: new Date().toISOString(),
  };
}

export { resolveFetchWindow };
